<template>
  <div>
    <a-modal
      title="采购入库记录"
      :visible="visible"
      :footer="null"
      @cancel="handleCancel"
      width="1200px"
      :centered="true"
    >
      <!-- 搜索条件 -->
      <div class="table-page-search-wrapper" style="margin-bottom: 16px">
        <a-form layout="inline">
          <a-row :gutter="24">
            <a-col :span="8">
              <a-form-item label="采购单号">
                <a-input v-model="queryParam.initNo" placeholder="请输入采购单号" allow-clear />
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item label="供应商">
                <a-input v-model="queryParam.supplierName" placeholder="请输入供应商名称" allow-clear />
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item label="状态">
                <a-select v-model="queryParam.status" placeholder="请选择状态" allow-clear style="width: 100%">
                  <a-select-option :value="1">待审核</a-select-option>
                  <a-select-option :value="2">已审核</a-select-option>
                  <a-select-option :value="3">已取消</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item>
                <a-button type="primary" @click="handleQuery">
                  <a-icon type="search" />查询
                </a-button>
                <a-button style="margin-left: 8px" @click="resetQuery">
                  <a-icon type="redo" />重置
                </a-button>
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
      </div>

      <!-- 记录列表 -->
      <a-table
        :loading="loading"
        rowKey="id"
        :columns="columns"
        :data-source="list"
        :pagination="pagination"
        @change="handleTableChange"
        size="middle"
      >
        <span slot="batchNo" slot-scope="text">
          <a-tag color="blue">{{ text }}</a-tag>
        </span>
        <span slot="supplierName" slot-scope="text">
          {{ text || '-' }}
        </span>
        <span slot="goodsCount" slot-scope="text">
          <a-tag color="green">{{ text }}</a-tag>
        </span>
        <span slot="totalStock" slot-scope="text">
          <a-tag color="orange">{{ text }}</a-tag>
        </span>
        <span slot="remark" slot-scope="text">
          {{ text || '-' }}
        </span>
        <span slot="createTime" slot-scope="text">
          {{ parseTime(text) }}
        </span>
        <span slot="status" slot-scope="text">
          <a-tag :color="getStatusColor(text)">{{ getStatusText(text) }}</a-tag>
        </span>
        <span slot="operation" slot-scope="text, record">
          <a @click="handleDetail(record)" style="margin-right: 8px">
            <a-icon type="eye" />查看详情
          </a>
          <a v-if="record.status === 1" @click="handleAudit(record)" style="margin-right: 8px" v-hasPermi="['retail:inventory:audit']">
            <a-icon type="check" />审核通过
          </a>
          <a v-if="record.status === 1" @click="handleReject(record)" v-hasPermi="['retail:inventory:audit']">
            <a-icon type="close" />审核拒绝
          </a>
        </span>
      </a-table>
    </a-modal>

    <!-- 明细弹窗 -->
    <a-modal
      :title="`采购明细 - ${currentRecord?.initNo || ''}`"
      :visible="detailVisible"
      :footer="null"
      @cancel="detailVisible = false"
      width="1000px"
      :centered="true"
    >
        <a-descriptions bordered :column="2" style="margin-bottom: 16px">
        <a-descriptions-item label="采购单号">{{ currentRecord?.initNo }}</a-descriptions-item>
        <a-descriptions-item label="供应商">{{ currentRecord?.supplierName || '-' }}</a-descriptions-item>
        <a-descriptions-item label="状态">
          <a-tag :color="getStatusColor(currentRecord?.status)">{{ getStatusText(currentRecord?.status) }}</a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="审核人">{{ currentRecord?.auditor || '-' }}</a-descriptions-item>
        <a-descriptions-item label="商品数量">{{ currentRecord?.totalGoods }}</a-descriptions-item>
        <a-descriptions-item label="采购总量">{{ currentRecord?.totalStock }}</a-descriptions-item>
        <a-descriptions-item label="总金额">{{ currentRecord?.totalAmount ? '¥' + parseFloat(currentRecord.totalAmount).toFixed(2) : '¥0.00' }}</a-descriptions-item>
        <a-descriptions-item label="备注" :span="2">{{ currentRecord?.remark || '-' }}</a-descriptions-item>
        <a-descriptions-item label="创建时间">{{ parseTime(currentRecord?.createTime) }}</a-descriptions-item>
        <a-descriptions-item label="审核时间">{{ parseTime(currentRecord?.auditTime) }}</a-descriptions-item>
      </a-descriptions>

      <a-table
        :loading="detailLoading"
        rowKey="id"
        :columns="detailColumns"
        :data-source="detailList"
        :pagination="false"
        size="small"
      >
        <span slot="goodsName" slot-scope="text, record">
          {{ text }} <a-tag v-if="record.skuName" color="orange">{{ record.skuName }}</a-tag>
        </span>
        <span slot="stock" slot-scope="text">
          <a-tag color="blue">{{ text }}</a-tag>
        </span>
        <span slot="purchasePrice" slot-scope="text">
          ¥{{ text ? parseFloat(text).toFixed(2) : '0.00' }}
        </span>
        <span slot="merchantPrice" slot-scope="text">
          ¥{{ text ? parseFloat(text).toFixed(2) : '0.00' }}
        </span>
      </a-table>
    </a-modal>
  </div>
</template>

<script>
import { getInitList, getInitItemList, auditInitRecord, rejectInitRecord } from '@/api/retail/inventory'
import moment from 'moment'

export default {
  name: 'InitRecordModal',
  data() {
    return {
      visible: false,
      loading: false,
      detailVisible: false,
      detailLoading: false,
      list: [],
      detailList: [],
      currentRecord: null,
      queryParam: {
        initNo: undefined,
        supplierId: undefined,
        status: undefined
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: total => `共 ${total} 条`
      },
      columns: [
        {
          title: '采购单号',
          dataIndex: 'initNo',
          width: 180,
          scopedSlots: { customRender: 'batchNo' },
          align: 'center'
        },
        {
          title: '供应商',
          dataIndex: 'supplierName',
          width: 200,
          scopedSlots: { customRender: 'supplierName' },
          align: 'center'
        },
        {
          title: '商品数量',
          dataIndex: 'totalGoods',
          width: 100,
          scopedSlots: { customRender: 'goodsCount' },
          align: 'center'
        },
        {
          title: '采购总量',
          dataIndex: 'totalStock',
          width: 100,
          scopedSlots: { customRender: 'totalStock' },
          align: 'center'
        },
        {
          title: '总金额',
          dataIndex: 'totalAmount',
          width: 120,
          align: 'center',
          customRender: (text) => text ? '¥' + parseFloat(text).toFixed(2) : '¥0.00'
        },
        {
          title: '状态',
          dataIndex: 'status',
          width: 100,
          scopedSlots: { customRender: 'status' },
          align: 'center'
        },
        {
          title: '创建时间',
          dataIndex: 'createTime',
          width: 180,
          scopedSlots: { customRender: 'createTime' },
          align: 'center'
        },
        {
          title: '操作',
          dataIndex: 'operation',
          width: 200,
          scopedSlots: { customRender: 'operation' },
          align: 'center',
          fixed: 'right'
        }
      ],
      detailColumns: [
        {
          title: '商品信息',
          dataIndex: 'goodsName',
          key: 'goodsName',
          width: 300,
          ellipsis: true,
          scopedSlots: { customRender: 'goodsName' },
          align: 'center'
        },
        {
          title: '采购数量',
          dataIndex: 'stock',
          key: 'stock',
          width: 100,
          scopedSlots: { customRender: 'stock' },
          align: 'center'
        },
        {
          title: '采购价(元)',
          dataIndex: 'purchasePrice',
          key: 'purchasePrice',
          width: 120,
          scopedSlots: { customRender: 'purchasePrice' },
          align: 'center'
        },
        {
          title: '商家拿货价(元)',
          dataIndex: 'merchantPrice',
          key: 'merchantPrice',
          width: 130,
          scopedSlots: { customRender: 'merchantPrice' },
          align: 'center'
        },
        {
          title: '预警库存',
          dataIndex: 'alarmStock',
          key: 'alarmStock',
          width: 100,
          align: 'center'
        }
      ]
    }
  },
  methods: {
    moment,
    parseTime(time) {
      return time ? moment(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    handleShow() {
      this.visible = true
      this.getList()
    },
    handleCancel() {
      this.visible = false
    },
    handleQuery() {
      this.pagination.current = 1
      this.getList()
    },
    resetQuery() {
      this.queryParam = {
        initNo: undefined,
        supplierId: undefined,
        status: undefined
      }
      this.pagination.current = 1
      this.getList()
    },
    handleTableChange(pagination) {
      this.pagination = pagination
      this.getList()
    },
    getList() {
      this.loading = true
      const params = {
        ...this.queryParam,
        current: this.pagination.current,
        size: this.pagination.pageSize
      }
      getInitList(params).then(response => {
        if (response.data) {
          this.list = response.data.records || []
          this.pagination.total = response.data.total || 0
        } else if (Array.isArray(response)) {
          this.list = response
        } else {
          this.list = []
          this.pagination.total = 0
        }
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    handleDetail(record) {
      this.currentRecord = record
      this.detailVisible = true
      this.detailLoading = true
      getInitItemList({
        initId: record.id
      }).then(response => {
        console.log('采购明细响应:', response)
        if (response.data) {
          this.detailList = Array.isArray(response.data) ? response.data : []
          console.log('解析后的明细列表:', this.detailList)
          if (this.detailList.length > 0) {
            console.log('第一条数据完整对象:', JSON.stringify(this.detailList[0], null, 2))
            console.log('purchasePrice:', this.detailList[0].purchasePrice, '类型:', typeof this.detailList[0].purchasePrice)
            console.log('merchantPrice:', this.detailList[0].merchantPrice, '类型:', typeof this.detailList[0].merchantPrice)
            console.log('stock:', this.detailList[0].stock, '类型:', typeof this.detailList[0].stock)
            console.log('alarmStock:', this.detailList[0].alarmStock, '类型:', typeof this.detailList[0].alarmStock)
          }
        } else if (Array.isArray(response)) {
          this.detailList = response
        } else {
          this.detailList = []
        }
        this.detailLoading = false
      }).catch(() => {
        this.detailLoading = false
      })
    },
    handleAudit(record) {
      this.$confirm({
        title: '确认审核通过',
        content: `确定要审核通过采购单【${record.initNo}】吗?`,
        onOk: () => {
          auditInitRecord({ initId: record.id }).then(() => {
            this.$message.success('审核通过成功')
            this.getList()
          }).catch(error => {
            this.$message.error('审核失败：' + (error.message || '未知错误'))
          })
        }
      })
    },
    handleReject(record) {
      this.$confirm({
        title: '确认审核拒绝',
        content: `确定要拒绝采购单【${record.initNo}】吗?`,
        onOk: () => {
          rejectInitRecord({ initId: record.id }).then(() => {
            this.$message.success('已拒绝该采购申请')
            this.getList()
          }).catch(error => {
            this.$message.error('操作失败：' + (error.message || '未知错误'))
          })
        }
      })
    },
    getStatusText(status) {
      const statusMap = {
        1: '待审核',
        2: '已审核',
        3: '已取消'
      }
      return statusMap[status] || '未知'
    },
    getStatusColor(status) {
      const colorMap = {
        1: 'orange',
        2: 'green',
        3: 'red'
      }
      return colorMap[status] || 'default'
    }
  }
}
</script>

<style scoped>
.table-page-search-wrapper {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
}
</style>
